import { useTranslation } from 'react-i18next';

const STEP_ICONS = ['ri-search-eye-line', 'ri-file-text-line', 'ri-code-s-slash-line', 'ri-user-follow-line', 'ri-exchange-line', 'ri-bar-chart-2-line'];
const STEP_COLORS = ['#FF8C42', '#FF2D78', '#FF8C42', '#FF2D78', '#FF8C42', '#FF2D78'];
const FEATURE_ICONS = ['ri-stock-line', 'ri-water-flash-line', 'ri-swap-line', 'ri-pie-chart-line'];

const COPY = {
  'zh-CN': {
    title: '端到端发行流程', desc: '从资产评估到二级市场流通，6 大关键环节全程托管，平均发行周期 10-18 周，全程符合目标辖区监管要求。',
    marketTitle: '二级市场流通', marketDesc: '代币发行完成后，提供多种二级流通渠道，满足不同投资者的流动性需求，让 RWA 代币真正成为可交易、可流通的数字资产。',
    steps: [
      ['资产尽调与评估', '专业团队对资产进行法律尽调、财务审计与市场估值，出具尽调报告，锁定代币化可行性', '2-4 周'],
      ['法律架构设计', '设计 SPV 架构，起草募集说明书、认购协议及托管安排，确保底层资产权益映射合法有效', '3-5 周'],
      ['智能合约开发', '基于 ERC-3643 开发合规代币合约，集成 KYC 白名单、转账限制、分红功能，三方安全审计', '4-6 周'],
      ['投资者认购', '搭建一级发行平台，投资者完成 KYC/AML，签署数字协议，完成资金托管后代币自动发放', '1-2 周'],
      ['二级市场上架', '将代币上架合规 STO 交易平台或 DEX 流动性池，提供 OTC 大宗交易撮合服务', '持续'],
      ['持续合规运营', '自动化监管报告、资产净值更新、分红派息，持续监控合规状态，支持年度审计', '持续'],
    ],
    features: [
      ['合规 STO 二级交易', '接入全球多家持牌证券型代币交易平台'],
      ['DEX 流动性池', '在 Uniswap V3 / Balancer 创建合规流动性'],
      ['OTC 大宗交易台', '机构级 OTC 询价，百万美元级块状交易'],
      ['分红自动化', '链上智能合约按持仓比例自动派息分红'],
    ],
  },
  en: {
    title: 'End-to-End Issuance Workflow', desc: 'From asset assessment to secondary-market circulation, we manage six key stages with typical issuance cycles of 10-18 weeks under target-jurisdiction compliance.',
    marketTitle: 'Secondary Market Circulation', marketDesc: 'After issuance, multiple circulation channels are provided to satisfy liquidity needs across investor profiles.',
    steps: [
      ['Due Diligence & Assessment', 'Legal due diligence, financial audit, and valuation to confirm tokenization feasibility.', '2-4 weeks'],
      ['Legal Structuring', 'SPV design, prospectus, subscription agreements, and custody setup.', '3-5 weeks'],
      ['Smart Contract Development', 'ERC-3643 compliant token contracts with KYC whitelist and transfer controls.', '4-6 weeks'],
      ['Investor Subscription', 'Primary issuance flow with KYC/AML and automated token allocation after custody.', '1-2 weeks'],
      ['Secondary Listing', 'Listing on compliant STO venues or DEX liquidity pools with OTC support.', 'Ongoing'],
      ['Continuous Compliance Ops', 'Automated reporting, NAV updates, and dividend distribution monitoring.', 'Ongoing'],
    ],
    features: [
      ['Compliant STO Trading', 'Connect to licensed security-token exchanges globally.'],
      ['DEX Liquidity Pools', 'Create compliant liquidity on Uniswap V3 / Balancer.'],
      ['OTC Block Desk', 'Institutional OTC quote flow for large block trades.'],
      ['Automated Dividends', 'Smart-contract driven distribution by holding ratio.'],
    ],
  },
  'zh-TW': {
    title: '端到端發行流程', desc: '從資產評估到二級市場流通，6 大關鍵環節全程託管，平均發行週期 10-18 週，全程符合目標轄區監管要求。',
    marketTitle: '二級市場流通', marketDesc: '代幣發行完成後，提供多種二級流通渠道，滿足不同投資者的流動性需求，讓 RWA 代幣真正成為可交易、可流通的數字資產。',
    steps: [
      ['資產盡調與評估', '專業團隊對資產進行法律盡調、財務審計與市場估值，出具盡調報告，鎖定代幣化可行性', '2-4 週'],
      ['法律架構設計', '設計 SPV 架構，起草募集說明書、認購協議及託管安排，確保底層資產權益映射合法有效', '3-5 週'],
      ['智能合約開發', '基於 ERC-3643 開發合規代幣合約，集成 KYC 白名單、轉賬限制、分紅功能，三方安全審計', '4-6 週'],
      ['投資者認購', '搭建一級發行平台，投資者完成 KYC/AML，簽署數字協議，完成資金託管後代幣自動發放', '1-2 週'],
      ['二級市場上架', '將代幣上架合規 STO 交易平台或 DEX 流動性池，提供 OTC 大宗交易撮合服務', '持續'],
      ['持續合規運營', '自動化監管報告、資產淨值更新、分紅派息，持續監控合規狀態，支持年度審計', '持續'],
    ],
    features: [
      ['合規 STO 二級交易', '接入全球多家持牌證券型代幣交易平台'],
      ['DEX 流動性池', '在 Uniswap V3 / Balancer 創建合規流動性'],
      ['OTC 大宗交易台', '機構級 OTC 詢價，百萬美元級塊狀交易'],
      ['分紅自動化', '鏈上智能合約按持倉比例自動派息分紅'],
    ],
  },
} as const;
function getLocale(lng: string) { if (lng.startsWith('en')) return 'en'; if (lng.startsWith('zh-TW')) return 'zh-TW'; return 'zh-CN'; }

export default function RwaIssuanceSection() {
  const { i18n } = useTranslation();
  const c = COPY[getLocale(i18n.resolvedLanguage || i18n.language || 'zh-CN')];
  return (
    <section id="issuance" className="bg-light-section py-24">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full"><i className="ri-file-list-3-line text-primary text-xs" /><span className="text-primary text-xs font-medium uppercase tracking-widest">Issuance &amp; Market</span></div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">{c.title}</h2>
          <p className="text-gray-500 text-base max-w-2xl mx-auto">{c.desc}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {c.steps.map((step, i) => (
            <div key={i} className="bg-white border border-primary/10 rounded-xl p-5 hover:border-primary/25 transition-all duration-200">
              <div className="flex items-center justify-between mb-4"><div className="flex items-center gap-3"><div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: `${STEP_COLORS[i]}12` }}><i className={`${STEP_ICONS[i]} text-sm`} style={{ color: STEP_COLORS[i] }} /></div><span className="text-3xl font-heading font-black opacity-10 text-gray-900">{String(i + 1).padStart(2, '0')}</span></div><span className="text-xs px-2 py-0.5 rounded-full border" style={{ borderColor: `${STEP_COLORS[i]}30`, color: STEP_COLORS[i] }}>{step[2]}</span></div>
              <h4 className="text-gray-800 font-medium text-sm mb-2">{step[0]}</h4>
              <p className="text-gray-500 text-xs leading-relaxed">{step[1]}</p>
            </div>
          ))}
        </div>
        <div id="market" className="bg-white border border-primary/10 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6"><div className="w-8 h-8 flex items-center justify-center"><i className="ri-stock-line text-primary text-lg" /></div><h3 className="text-gray-900 font-heading font-bold text-xl">{c.marketTitle}</h3></div>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">{c.marketDesc}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {c.features.map((f, i) => (
              <div key={i} className="bg-light-alt rounded-xl p-4 border border-primary/10 hover:border-primary/25 transition-all duration-200"><div className="w-8 h-8 flex items-center justify-center mb-3"><i className={`${FEATURE_ICONS[i]} text-primary text-lg`} /></div><h4 className="text-gray-800 text-sm font-medium mb-1">{f[0]}</h4><p className="text-gray-500 text-xs">{f[1]}</p></div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
